import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import {
  Typography,
  TableWrapperFooter,
  TableIconCell,
  TableDots,
  Pagination,
  Button,
  enqueueSnackbar,
  assignYardsAndClientsModal,
  SimpleTable,
} from '../../../design-system';
import { DashboardHeader } from '../../dashboard';
import { CompanyFilters } from '../CompanyFilters';
import { useStore } from '../../../context';
import {
  useStatesWithFilterCounter,
  usePaginationState,
  useYardsQuery,
  useAssignClientsMutation,
} from '../../../hooks';
import { showToRoles, formatPhoneNumber } from '../../../utils';
import { TABLE, MESSAGES } from '../../../const';
import { i18n } from '../../../i18n';
import { useNavigate } from 'react-router-dom';

interface YardsProps {
  innerScroll?: boolean;
}

export const YardsPage = styled.div<YardsProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

export const YardsHeader = ({ totalCount }: any) => {
  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.yards.title', { totalCount })}
      </Typography>
      <Button size={'medium'} to={'/dashboard/yards/add'}>
        {i18n.t('button.addYard')}
      </Button>
    </DashboardHeader>
  );
};

export const YardsBody = ({ setTotalCount }: any) => {
  const navigate = useNavigate();
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const { mutate } = useAssignClientsMutation();
  const [openDotsMenuIndex, setOpenMenuDotsSelect] = useState(null);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter({
    companyLegalName: i18n.t('label.all'),
    yardCompanyId: 'All',
    name: '',
    streetAddress: '',
    city: '',
    zipCode: '',
    country: '',
    phoneNumber: '',
  });

  const { data = { data: [], totalCount: 0 }, isFetching } = useYardsQuery({
    ...filteredStates,
    take,
    skip,
  });

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
  }, [isFetching]);

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setOpenMenuDotsSelect(i);

    setOpenMenuDotsSelect(null);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handlePageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = async (action: any, id: any, yardName: string) => {
    if (
      action !== 'add-clients' &&
      action !== 'reactivate-clients' &&
      action !== 'deactivate-clients'
    )
      return;

    Object.keys(assignYardsAndClientsModal).forEach((key) => {
      if (key !== 'open') delete assignYardsAndClientsModal[key];
    });
    assignYardsAndClientsModal.id = id;
    assignYardsAndClientsModal.assignType = 'clients';
    assignYardsAndClientsModal.action = action;
    assignYardsAndClientsModal.fc = (props: any) =>
      mutate(
        { ...props, yardName, action },
        {
          onSuccess: () => {
            if (action === 'add-clients')
              enqueueSnackbar(MESSAGES.YARD.SUCCESS.ASSIGN_CLIENTS, 'success');
            if (action === 'reactivate-clients')
              enqueueSnackbar(MESSAGES.YARD.SUCCESS.REACTIVATED_CLIENTS, 'success');
            if (action === 'deactivate-clients')
              enqueueSnackbar(MESSAGES.YARD.SUCCESS.DEACTIVATED_CLIENTS, 'success');
          },
          onError: () => {
            if (action === 'add-clients')
              enqueueSnackbar(MESSAGES.YARD.ERROR.CLIENTS_NOT_ASSIGNED, 'error');
            if (action === 'reactivate-clients')
              enqueueSnackbar(MESSAGES.YARD.ERROR.CLIENTS_NOT_REACTIVATED, 'error');
            if (action === 'deactivate-clients')
              enqueueSnackbar(MESSAGES.YARD.ERROR.CLIENTS_NOT_DEACTIVATED, 'error');
          },
        },
      );
    assignYardsAndClientsModal.open();
  };

  const handleRowClick = (to: string) => {
    if (openDotsMenuIndex !== null) return;
    navigate(to);
  };

  return (
    <SimpleTable
      data={data}
      filters={() => <CompanyFilters setStates={handleFilterChange} {...states} />}
      tableRowProps={(row, i) => ({
        key: row.id + i,
        onClick: () => handleRowClick(`/dashboard/yards/${row.id}`),
      })}
      columns={[
        {
          column: 1,
          headText: () => i18n.t('label.actions'),
          body: (row, i) => (
            <TableIconCell component={'th'} align={'left'}>
              <TableDots
                open={openDotsMenuIndex === i}
                onToggle={() => handleDotsToggle(i)}
                options={[
                  { label: i18n.t('label.addClients'), value: 'add-clients' },
                  { label: i18n.t('label.reactivateClients'), value: 'reactivate-clients' },
                  { label: i18n.t('label.deactivateClients'), value: 'deactivate-clients' },
                ]}
                onSelect={(val: any) => {
                  handleDotsOptionSelect(val, row.id, row.name);
                }}
              />
            </TableIconCell>
          ),
        },
        {
          headText: () => i18n.t('common.yardCompany'),
          bodyText: (row) => row.yardCompany.legalName,
          isHidden: () => !showToRoles(['SUPER_ADMIN'], user),
        },
        {
          headText: () => i18n.t('label.yardName'),
          bodyText: (row) => row.name,
        },
        {
          headText: () => i18n.t('label.country'),
          bodyText: (row) => row.address.country,
        },
        {
          headText: () => i18n.t('label.city'),
          bodyText: (row) => row.address.city,
        },
        {
          headText: () => i18n.t('label.streetAddress'),
          bodyText: (row) => row.address.streetAddress,
        },
        {
          headText: () => i18n.t('label.zipCode'),
          bodyText: (row) => row.address.zipCode,
        },
        {
          headText: () => i18n.t('label.code'),
          bodyText: (row) => row.code,
        },
        {
          headText: () => i18n.t('label.phoneNumber'),
          bodyText: (row) => formatPhoneNumber(row.phoneNumber),
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handlePageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
